import { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Timer, CalendarClock, Sparkles, Loader2, RefreshCw, CheckCircle2, Plus, Send, MessageSquare, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Make the last day revision-only",
  "Add more practice questions",
  "I have tennis tomorrow, lighten that day",
  "Focus more on my weak topics",
];

const targetOf = (e) => new Date(`${e.date}T${(e.time && e.time.length ? e.time : "08:00")}:00`);

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(target.getTime() - now, 0);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
    done: diff === 0,
  };
}

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
      <span className="text-2xl sm:text-3xl font-heading font-bold tabular-nums text-white">{String(value).padStart(2, "0")}</span>
    </div>
    <span className="text-[10px] uppercase tracking-widest text-indigo-100 mt-2">{label}</span>
  </div>
);

export default function ExamPrep() {
  const [exams, setExams] = useState([]);
  const [selId, setSelId] = useState("");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addedDays, setAddedDays] = useState([]);
  const [chat, setChat] = useState([]);
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    api.get("/exams").then((r) => {
      const today = new Date(new Date().toDateString());
      const upcoming = r.data.filter((e) => new Date(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date));
      setExams(upcoming);
      if (upcoming[0]) setSelId(upcoming[0].id);
    });
  }, []);

  const selected = useMemo(() => exams.find((e) => e.id === selId), [exams, selId]);
  const target = selected ? targetOf(selected) : new Date();
  const cd = useCountdown(target);

  useEffect(() => { setPlan(null); setAddedDays([]); setChat([]); }, [selId]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat, refining]);

  const refine = async (text) => {
    const msg = (text ?? refineInput).trim();
    if (!msg || refining || !selected) return;
    setRefineInput("");
    setChat((c) => [...c, { role: "user", content: msg }]);
    setRefining(true);
    try {
      const r = await api.post("/study-plan/refine", { exam_id: selected.id, message: msg });
      setPlan(r.data.plan);
      setAddedDays([]);
      setChat((c) => [...c, { role: "assistant", content: r.data.reply }]);
    } catch {
      setChat((c) => [...c, { role: "assistant", content: "Sorry, I couldn't update the plan just now. Please try again." }]);
      toast.error("Could not update the plan. Try again.");
    } finally {
      setRefining(false);
    }
  };

  const addDay = async (d, i) => {
    if (!selected) return;
    try {
      await Promise.all((d.tasks || []).map((t) =>
        api.post("/tasks", { title: t, subject: selected.subject, due_date: d.date, priority: "medium" })
      ));
      setAddedDays((prev) => [...prev, i]);
      toast.success(`${(d.tasks || []).length} tasks added for ${new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`);
    } catch {
      toast.error("Could not add tasks. Try again.");
    }
  };

  const generate = async (regenerate = false) => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await api.post("/study-plan", { exam_id: selected.id, regenerate });
      setPlan(r.data.plan);
      if (regenerate) { setAddedDays([]); toast.success("Fresh plan generated"); }
    } catch {
      toast.error("Could not generate the study plan. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (exams.length === 0) {
    return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">No upcoming exams. Add one under Exams & Events.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-slate-700 font-medium"><Timer className="w-4 h-4 text-indigo-500" /> Preparing for</div>
        <Select value={selId} onValueChange={setSelId}>
          <SelectTrigger data-testid="exam-select" className="rounded-xl sm:w-96"><SelectValue /></SelectTrigger>
          <SelectContent>
            {exams.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.subject} — {new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Countdown hero */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-600 p-7 sm:p-9 text-white" data-testid="countdown-hero">
        <div className="absolute -right-10 -top-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">{selected?.subject}</p>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold mt-1">{selected?.topic || "Assessment"}</h2>
        <p className="text-indigo-100 text-sm mt-1 flex items-center gap-1">
          <CalendarClock className="w-4 h-4" /> {target.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
        <div className="flex gap-3 sm:gap-4 mt-6">
          <Unit value={cd.days} label="Days" />
          <Unit value={cd.hours} label="Hours" />
          <Unit value={cd.mins} label="Mins" />
          <Unit value={cd.secs} label="Secs" />
        </div>
      </motion.div>

      {/* Study plan */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-500" /> Day-by-day study plan</h3>
          {plan ? (
            <Button data-testid="regen-plan-btn" onClick={() => generate(true)} disabled={loading} variant="outline" size="sm" className="rounded-lg">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</>}
            </Button>
          ) : (
            <Button data-testid="gen-plan-btn" onClick={() => generate(false)} disabled={loading} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Building plan...</> : <><Sparkles className="w-4 h-4 mr-1" /> Generate AI plan</>}
            </Button>
          )}
        </div>

        {!plan && !loading && (
          <p className="text-sm text-slate-400">Tap "Generate AI plan" and Claude will map out what to revise each day until the exam.</p>
        )}

        {plan && (
          <div className="space-y-3" data-testid="study-plan">
            {plan.map((d, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className="flex gap-4 rounded-xl border border-slate-100 p-4">
                <div className="w-14 shrink-0 text-center">
                  <div className="w-11 h-11 mx-auto rounded-xl bg-indigo-50 flex flex-col items-center justify-center">
                    <span className="text-[9px] font-bold text-indigo-500 uppercase">{new Date(d.date).toLocaleDateString("en-US", { month: "short" })}</span>
                    <span className="text-base font-bold text-indigo-700 leading-none">{new Date(d.date).getDate()}</span>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-slate-900 text-sm">{d.focus}</p>
                    <Button
                      data-testid={`add-day-tasks-${i}`}
                      onClick={() => addDay(d, i)}
                      disabled={addedDays.includes(i)}
                      variant="outline" size="sm"
                      className={`rounded-lg shrink-0 h-7 text-xs ${addedDays.includes(i) ? "text-emerald-600 border-emerald-200" : ""}`}
                    >
                      {addedDays.includes(i) ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Added</> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add to tasks</>}
                    </Button>
                  </div>
                  <ul className="mt-1.5 space-y-1">
                    {(d.tasks || []).map((t, ti) => (
                      <li key={ti} className="text-sm text-slate-600 flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {plan && (
          <div className="mt-5 pt-5 border-t border-slate-100" data-testid="plan-chat">
            <h4 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1"><Wand2 className="w-4.5 h-4.5 text-purple-500" /> Rework this plan with AI</h4>
            <p className="text-xs text-slate-500 mb-3">Tell the tutor what to change and it will rewrite the plan above.</p>

            {chat.length > 0 && (
              <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
                {chat.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {refining && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                      {[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full bg-slate-400 thinking-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}

            {chat.length === 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="plan-suggestion" onClick={() => refine(s)} disabled={refining}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-purple-300 hover:bg-purple-50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); refine(); }} className="flex gap-2">
              <Input data-testid="plan-chat-input" placeholder="e.g. Add an extra day of practice papers" value={refineInput} onChange={(e) => setRefineInput(e.target.value)} className="rounded-xl" />
              <Button data-testid="plan-chat-send" type="submit" disabled={refining} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 px-4">
                {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
